<template>
  <div class="login-page">
    <div class="login-shell" v-if="!user">
      <div class="login-card">
        <div class="card-content">
          <transition name="fade">
            <div class="error-chip" v-if="error">{{ error }}</div>
          </transition>
          <transition name="fade">
            <div class="success-chip" v-if="success">{{ success }}</div>
          </transition>

          <header class="login-header">
            <img src="../../../../brand/pwa-icons/icon-192x192.png" alt="easyHMO" class="brand-logo" />
            <h2 class="title">Sign in</h2>
            <p class="subtitle">Continue to easyHMO.</p>
          </header>

          <main>
            <form @submit.prevent="handleNext" class="modern-form">
              <div class="input-group">
                <input type="text" v-model="username" required placeholder=" " @focus="handleFocus" autocomplete="username" />
                <label>Username</label>
              </div>

              <transition name="fade">
                <div class="input-group delay password-group" v-if="isChecked">
                  <div class="password-control">
                    <input
                      :type="showPassword ? 'text' : 'password'"
                      v-model="password"
                      required
                      placeholder=" "
                      autocomplete="current-password"
                      class="password-input"
                    />

                    <label>Password</label>
                    <button
                      type="button"
                      class="password-toggle"
                      :title="showPassword ? 'Hide password' : 'Show password'"
                      :aria-label="showPassword ? 'Hide password' : 'Show password'"
                      @click="showPassword = !showPassword"
                    >
                      <i class="material-icons">{{ showPassword ? "visibility_off" : "visibility" }}</i>
                    </button>
                  </div>
                  <div class="form-options">
                    <a href="#" class="forgot-link" @click.prevent="handleForgot">Forgot password?</a>
                  </div>
                </div>
              </transition>
              <button type="submit" class="glow-button" :disabled="isSubmitting">
                <span v-if="!isSubmitting">{{ isChecked ? "Submit" : "Next" }}</span>
                <span v-else class="login-button-loading">
                  <span class="login-button-spinner" aria-hidden="true"></span>
                  <span>{{ isChecked ? "Signing in..." : "Checking..." }}</span>
                </span>
              </button>
              <transition name="notice-slide">
                <div class="forgot-notice" v-if="forgotNotice">
                  <span>{{ forgotNotice }}</span>
                  <button type="button" class="forgot-notice-close" @click="clearForgotNotice" aria-label="Dismiss password reset note">
                    <i class="material-icons">close</i>
                  </button>
                </div>
              </transition>
            </form>
          </main>
        </div>
      </div>

      <p class="learn-more-text">
        Learn more about
        <a class="learn-more-link" href="https://easyhmo.com.ng/" target="_blank" rel="noopener noreferrer">easyHMO</a>
      </p>
    </div>

    <div class="login-update-shell" v-if="user">
      <UpdateUserDiv :user="user" :temporary-password="password" @updated="handleUpdate" />
    </div>

    <!-- <footer class="login-footer">
      <p>easyHMO <span class="footer-dot">•</span> 5.0</p>
    </footer> -->
  </div> 
</template>

<script>
import { onMounted, ref } from "vue";
import postItem from "@/composables/postItem";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { clearCache } from "@/composables/cache/logout";
import { addModules } from "@/composables/cache/modules";
import UpdateUserDiv from "@/components/login/UpdateUserComponent.vue";
import { ABBR } from "@/laravel/config";

export default {
  name: "LoginHome",
  components: { UpdateUserDiv },
  setup() {
    const isSubmitting = ref(false);
    const user = ref(null);
    const username = ref(null);
    const password = ref(null);
    const showPassword = ref(false);
    const isChecked = ref(false);
    const unregUser = ref(null);
    const error = ref(null);
    const success = ref(null);
    const forgotNotice = ref(null);
    const adminLabel = `${ABBR || "HMO"} Admin`;

    // vue router
    const router = useRouter();

    // store
    const store = useStore();

    // start
    onMounted(() => {
      // page name
      document.title = "Login | easyHMO";
    });

    // reset login
    onMounted(() => {
      if (!store.state.user) return;
      // clean up
      clearCache();
    });

    // get in
    const getIn = (userVal) => {

      // load login history
      store.commit("loadLoginHistory", userVal?.email);

      // load user
      store.commit("loadUser", userVal);
      store.commit("loadDashboardFreshSignin", true);

      // refer loading
      router.push({
        name: "loading",
      });

    }

    // clear
    const clearMessages = () => {
      error.value = null;
      success.value = null;
      forgotNotice.value = null;
      isSubmitting.value = false
    }

    const getLoginRequestMessage = (type, stage = "signin") => {
      if (type === "validation") {
        return stage === "check" ? "Please enter your username." : "Please enter your username and password.";
      }

      if (type === "network") {
        return "Unable to reach easyHMO. Please check your connection and try again.";
      }

      return "Sign in is temporarily unavailable. Please try again shortly.";
    }

    // handle next
    const handleNext = () => {
    // clear messages
    clearMessages();
    // start
      if (!isChecked.value) {
        // check loginHistory
        if (store.state?.loginHistory === username.value) {
          // user exists
          isChecked.value = true;
        } else {
          // check user
          handleUserCheck();
        }
      } else {
        // sign in
        handleSignIn();
      }
    };

    // signCheck
    const handleUserCheck = () => {
      // start
      isSubmitting.value = true;

      (async () => {
        const { res, errorType } = await postItem("authority/signin/user/check", {
          username: username.value,
        });
        if (res.value) {
          if (res.value?.success) {
            // user exists
            isChecked.value = true;
          } else {
            // user doesn't exist
            // start
            let newVal = null;
            // prepare for account creation
            if (res.value?.client) {
              // client
              let clientModules = addModules()
                .filter(x => x.type === "client")
                .map(item => item.to);

              let clientSchemes = res.value.schemes.filter(x => x.type === "p")
                .map(item => item.id);

              newVal = {
                email: res.value?.client?.email,
                tag: res.value?.client?.id,
                schemes: clientSchemes,
                modules: clientModules,
                type: "client",
              }

            } else if (res.value?.provider) {
              // provider
              let providerModules = addModules()
                .filter(x => x.type === "provider")
                .map(item => item.to);

              let providerSchemes = res.value.schemes
                .map(item => item.id);

              newVal = {
                email: res.value?.provider?.email,
                tag: res.value?.provider?.id,
                schemes: providerSchemes,
                modules: providerModules,
                type: "provider",
              }
            } else if (res.value?.principal) {
              // principal
              let principalModules = addModules()
                .filter(x => x.type === "principal")
                .map(item => item.to);

              let principalSchemes = res.value.schemes.filter(x => x.type === "p")
                .map(item => item.id);

              newVal = {
                email: res.value?.principal?.email,
                tag: res.value?.principal?.id,
                schemes: principalSchemes,
                modules: principalModules,
                type: "principal",
              }
            } else {
              error.value = `We could not find an account for that username. Please contact ${adminLabel}.`;
            }

            // set unreg user
            if (newVal) {
              const { res: resCreate } = await postItem("authority/signin/user/create", {
                ...newVal,
              });
              if (resCreate.value) {
                isChecked.value = true;
                success.value = `Check your email (${newVal.email}) for your new login password.`;
                unregUser.value = resCreate.value.data;
              } else {
                error.value = `We could not prepare this account right now. Please contact ${adminLabel}.`;
              }
            }
          }
        } else {
          error.value = getLoginRequestMessage(errorType.value, "check");
        }
        // end end
        isSubmitting.value = false;
      })();

    };

    // handle 
    const handleFocus = () => {
      // clear messages
      clearMessages();
      // reset
      isChecked.value = false;
      password.value = null;
    }

    // signIn
    const handleSignIn = () => {
      // start
      isSubmitting.value = true;
      error.value = null;

      (async () => {
        const { res, errorType } = await postItem("authority/signin", {
          username: username.value,
          password: password.value,
        });
        if (res.value) {
          if (res.value?.success) {

            if (res.value.data.status === 'a') {

              // moveee
              getIn(res.value.data);

            } else if (res.value.data.status === 'p') {

              // load user for update
              user.value = res.value.data;

            } else {
              error.value = `This account cannot be used right now. Please contact ${adminLabel}.`;
            }

          } else if (res.value?.reason === "account_unavailable") {
            error.value = `This account cannot be used right now. Please contact ${adminLabel}.`;
          } else {
            error.value = "We could not sign you in with those details. Please check and try again.";
          }

        } else {
          error.value = getLoginRequestMessage(errorType.value, "signin");
        }
        // end end
        isSubmitting.value = false;
      })();

    };

    // send updated folk
    const handleUpdate = (userVal) => {
      getIn(userVal);
    }

    // handle forgot
    const handleForgot = () => {
      // clear messages
      clearMessages();
      forgotNotice.value = `Contact ${adminLabel} for password reset.`;
    }

    const clearForgotNotice = () => {
      forgotNotice.value = null;
    }

    return {
      handleNext,
      handleSignIn,
      handleUpdate,
      handleForgot,
      handleFocus,
      clearForgotNotice,
      isSubmitting,
      user,
      username,
      password,
      isChecked,
      unregUser,
      error,
      success,
      forgotNotice,
      showPassword,
    };
  },
};
</script>

<style scoped>
.progress {
  height: 2px;
  margin: 6px 0 0;
  background: #e0f2f1;
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease,
    max-height 220ms ease,
    margin-bottom 220ms ease;
  overflow: hidden;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  max-height: 0;
  margin-bottom: 0;
  transform: translateY(-6px);
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  max-height: 96px;
  transform: translateY(0);
}

.notice-slide-enter-active,
.notice-slide-leave-active {
  transition:
    opacity 180ms ease,
    transform 200ms ease,
    max-height 220ms ease,
    margin-top 220ms ease;
  overflow: hidden;
}

.notice-slide-enter-from,
.notice-slide-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
  transform: translateY(-6px);
}

.notice-slide-enter-to,
.notice-slide-leave-from {
  opacity: 1;
  max-height: 72px;
  transform: translateY(0);
}

:global(.app-shell:has(.login-page)) {
  min-height: 100vh;
  min-height: 100svh;
}

:global(.app-shell:has(.login-page) main) {
  min-height: 100vh;
  min-height: 100svh;
  padding-bottom: 0;
}

:global(.app-shell:has(.login-page) .app-bottom-spacer) {
  display: none;
}

.login-page {
  min-height: 100vh;
  min-height: 100svh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: clamp(14px, 3vh, 26px) 16px;
  background: #ffffff;
  font-family: "Inter", "Roboto", Arial, sans-serif;
}

.login-shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 410px;
  height: auto;
  min-height: 0;
  transform: translateY(calc((100svh - 100% - 36px) / 2 - 22px));
}

.login-card {
  flex: 0 0 auto;
  width: 100%;
  height: auto !important;
  min-height: 0 !important;
  background: #ffffff;
  border: 1px solid #dadce0;
  border-radius: 8px;
}

.login-card .card-content {
  box-sizing: border-box;
  height: auto !important;
  min-height: 0 !important;
  padding: clamp(24px, 3.4vh, 32px) 38px clamp(22px, 3vh, 28px);
}

.login-card main,
.modern-form {
  display: block;
  height: auto !important;
  min-height: 0 !important;
  margin: 0;
}

.login-header {
  margin-bottom: clamp(24px, 3.4vh, 32px);
}

.brand-logo {
  display: block;
  width: clamp(52px, 6vh, 60px);
  height: clamp(52px, 6vh, 60px);
  object-fit: contain;
  border-radius: 14px;
  margin-bottom: clamp(14px, 2.2vh, 18px);
}

.title {
  margin: 0;
  color: #202124;
  font-size: 1.55rem;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0;
}

.subtitle {
  margin: 6px 0 0;
  color: #5f6368;
  font-size: 0.95rem;
}

.input-group {
  position: relative;
  margin-bottom: clamp(12px, 1.9vh, 16px);
}

.input-group input {
  width: 100%;
  height: 50px;
  box-sizing: border-box;
  padding: 19px 14px 7px;
  background: #ffffff;
  border: 1px solid #dadce0;
  border-radius: 4px;
  color: #202124;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.password-control {
  position: relative;
  width: 100%;
}

.password-control .password-input {
  box-sizing: border-box !important;
  padding-right: 44px !important;
  width: 100% !important;
}

.password-toggle {
  -webkit-appearance: none;
  align-items: center;
  appearance: none;
  background: transparent !important;
  background-color: transparent !important;
  border: 0 !important;
  border-radius: 0;
  box-shadow: none !important;
  color: #5f6368;
  cursor: pointer;
  display: inline-flex;
  height: 24px;
  justify-content: center;
  line-height: 1;
  margin: 0;
  min-height: 0;
  padding: 0;
  position: absolute;
  right: 8px;
  top: 24px;
  transform: translateY(-50%);
  width: 28px;
  z-index: 2;
}

.password-toggle:hover,
.password-toggle:focus,
.password-toggle:active {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  color: #303f9f;
  outline: none;
}

.password-toggle i {
  display: block;
  font-size: 20px;
  line-height: 1;
  pointer-events: none;
}

.input-group input:focus {
  border-color: #303f9f;
  box-shadow: inset 0 0 0 1px #303f9f;
}

.input-group input:-webkit-autofill,
.input-group input:-webkit-autofill:hover,
.input-group input:-webkit-autofill:focus,
.input-group input:-webkit-autofill:active {
  -webkit-text-fill-color: #202124;
  caret-color: #202124;
  box-shadow: 0 0 0 1000px #ffffff inset;
  transition: background-color 9999s ease-in-out 0s;
}

.input-group label {
  position: absolute;
  left: 14px;
  top: 17px;
  padding: 0 4px;
  background: #ffffff;
  color: #5f6368;
  font-size: 0.95rem;
  pointer-events: none;
  transition: transform 0.2s ease, color 0.2s ease;
}

.input-group input:focus+label,
.input-group input:not(:placeholder-shown)+label {
  transform: translate(-4px, -21px) scale(0.82);
  transform-origin: left top;
  color: #5f6368;
}

.password-control input:focus+label,
.password-control input:not(:placeholder-shown)+label {
  transform: translate(-4px, -21px) scale(0.82);
  transform-origin: left top;
  color: #5f6368;
}

.delay {
  transition: 0.2s;
}

.glow-button {
  width: 100%;
  height: 46px;
  padding: 0 18px;
  background: #303f9f;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease;
}

.glow-button:hover {
  background: #283593;
}

.glow-button:disabled {
  cursor: default;
  opacity: 0.75;
}

.login-button-loading {
  align-items: center;
  display: inline-flex;
  gap: 10px;
  justify-content: center;
}

.login-button-spinner {
  animation: loginButtonSpin 760ms linear infinite;
  border: 2px solid rgba(255, 255, 255, 0.38);
  border-top-color: #ffffff;
  border-radius: 50%;
  box-sizing: border-box;
  display: inline-block;
  height: 18px;
  width: 18px;
}

@keyframes loginButtonSpin {
  to {
    transform: rotate(360deg);
  }
}

.modern-form {
  padding-bottom: 8px;
}

.error-chip {
  background: #ffebee;
  color: #b71c1c;
  padding: 11px 13px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #ffcdd2;
  font-size: 0.92rem;
}

.success-chip {
  background: #e8f5e9;
  color: #1b5e20;
  padding: 11px 13px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #c8e6c9;
  font-size: 0.92rem;
}

.form-options {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.forgot-link {
  color: #303f9f;
  font-size: 0.86rem;
  font-weight: 600;
  text-decoration: none;
}

.forgot-link:hover {
  color: #1a237e;
}

.forgot-notice {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #d9e2ec;
  border-radius: 6px;
  color: #405261;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  line-height: 1.35;
  margin-top: 14px;
  padding: 11px 11px 11px 13px;
  font-size: 0.88rem;
  font-weight: 600;
}

.forgot-notice-close {
  align-items: center;
  background: transparent;
  border: 0;
  color: #607d8b;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 26px;
  justify-content: center;
  padding: 0;
  width: 26px;
}

.forgot-notice-close i {
  font-size: 18px;
  line-height: 18px;
}

.forgot-notice-close:hover {
  color: #263238;
}

.login-update-shell {
  width: min(100%, 640px);
}

.learn-more-text {
  margin: 16px 0 0;
  color: #5f6368;
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
}

.learn-more-link {
  color: #303f9f;
  font-weight: 600;
  text-decoration: none;
}

.learn-more-link:hover {
  color: #1a237e;
  text-decoration: underline;
}

@media (max-width: 992px) {
  .login-card {
    border: 0;
  }
}

@media (max-width: 600px) {
  .login-page {
    align-items: flex-start;
    padding: 14px;
  }

  .login-card .card-content {
    padding: 24px 24px 22px;
  }

  .title {
    font-size: 1.65rem;
  }
}
</style>
